/**
 * Report Mock 数据
 * SLA 履约报表 和 支付发票报表 的 Mock 数据
 */

import type { ApiResponse, PageData } from '../../types/common';

// Mock 工具函数
function mockSuccess<T>(data: T): ApiResponse<T> {
  return {
    success: true,
    errorMessage: null,
    flag: 'success',
    data,
    traceId: `mock-${Date.now()}`,
  };
}

// SLA 履约报表 DTO
export interface SLAFulfillmentDTO {
  orderId: string;
  channel: string;
  paymentMethod: string;
  orderCreateTime: string;
  pickingTimeSpent: number;     // 拣货耗时(分钟)
  sortPackTime: number;         // 分拣打包耗时(分钟)
  slaDeadline: number;          // SLA 时限(分钟)
  timeoutMargin: number;        // 超时差值(分钟, 负数=未超时)
  orderEndStatus: string;       // Completed / Canceled / Returned / In Progress
}

// 支付发票报表 DTO
export interface PaymentInvoiceDTO {
  tillDate: string;
  storeNo: string;
  storeName: string;
  orderNumber: string;
  paymentType: string;
  amount: number;
  posNo: string;
  channel: string;
}

// SLA 报表查询参数
export interface SLAFulfillmentQueryParams {
  currentPage: number;
  pageSize: number;
  orderCreateDateStart?: string;
  orderCreateDateEnd?: string;
  channel?: string;
  paymentMethod?: string;
  isTimeout?: boolean | string;
  orderEndStatus?: string;
}

// 支付发票报表查询参数
export interface PaymentInvoiceQueryParams {
  currentPage: number;
  pageSize: number;
  tillDateStart?: string;
  tillDateEnd?: string;
  storeNo?: string;
  storeName?: string;
  paymentType?: string;
  orderNumber?: string;
}

// SLA 报表 Mock 数据
const slaFulfillmentData: SLAFulfillmentDTO[] = (() => {
  const channels = ['LOTUS', 'AMAZE'];
  const paymentMethods = ['Cash', 'O2O Instant CP', 'O2O Credit Online', 'O2O TMN at Door', 'O2O Voucher Online', 'O2O Member CP'];
  const statuses = ['Completed', 'Canceled', 'Returned', 'In Progress'];
  const result: SLAFulfillmentDTO[] = [];
  for (let i = 1; i <= 80; i++) {
    const pickingTime = 2 + Math.random() * 8;
    const sortPackTime = 1 + Math.random() * 5;
    const slaDeadline = 10;
    const totalTime = pickingTime + sortPackTime;
    result.push({
      orderId: `ORD-2026032${Math.floor(i / 30) + 1}-${String(i).padStart(3, '0')}`,
      channel: channels[Math.floor(Math.random() * channels.length)],
      paymentMethod: paymentMethods[Math.floor(Math.random() * paymentMethods.length)],
      orderCreateTime: `2026-03-2${Math.floor(i / 30) + 1} ${String(8 + Math.floor(i / 8)).padStart(2, '0')}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}:00`,
      pickingTimeSpent: Math.round(pickingTime * 100) / 100,
      sortPackTime: Math.round(sortPackTime * 100) / 100,
      slaDeadline,
      timeoutMargin: Math.round((totalTime - slaDeadline) * 100) / 100,
      orderEndStatus: statuses[Math.floor(Math.random() * statuses.length)],
    });
  }
  return result;
})();

// 支付发票报表 Mock 数据
const paymentInvoiceData: PaymentInvoiceDTO[] = (() => {
  const paymentTypes = ['Cash', 'O2O Instant CP', 'O2O Credit Online', 'O2O TMN at Door', 'O2O Voucher Online', 'O2O Member CP'];
  const storeNames = ['Lotus Store Bangna', 'Lotus Store Rama 9', 'Lotus Store Silom', 'Lotus Store Sukhumvit'];
  const storeNos = ['ST001', 'ST002', 'ST003', 'ST004'];
  const channelList = ['Lotus', 'Amaze'];
  const result: PaymentInvoiceDTO[] = [];
  for (let i = 1; i <= 60; i++) {
    const storeIdx = Math.floor(Math.random() * storeNames.length);
    result.push({
      tillDate: `2026-03-${String(20 + Math.floor(i / 15)).padStart(2, '0')}`,
      storeNo: storeNos[storeIdx],
      storeName: storeNames[storeIdx],
      orderNumber: `ORD-${String(20260320 + Math.floor(i / 15))}-${String(i).padStart(3, '0')}`,
      paymentType: paymentTypes[Math.floor(Math.random() * paymentTypes.length)],
      amount: Math.round((100 + Math.random() * 5000) * 100) / 100,
      posNo: Math.random() > 0.5 ? '106' : '126',
      channel: channelList[Math.floor(Math.random() * channelList.length)],
    });
  }
  return result;
})();

/**
 * Mock 查询 SLA 履约报表
 */
export function mockPageSLAFulfillment(
  params: SLAFulfillmentQueryParams,
): Promise<ApiResponse<PageData<SLAFulfillmentDTO>>> {
  return new Promise((resolve) => {
    setTimeout(() => {
      let filtered = [...slaFulfillmentData];

      if (params.channel) {
        filtered = filtered.filter((item) => item.channel === params.channel);
      }
      if (params.paymentMethod) {
        filtered = filtered.filter((item) => item.paymentMethod === params.paymentMethod);
      }
      if (params.orderEndStatus) {
        filtered = filtered.filter((item) => item.orderEndStatus === params.orderEndStatus);
      }
      if (params.isTimeout !== undefined && params.isTimeout !== '') {
        const isTimeout = params.isTimeout === true || params.isTimeout === 'true';
        filtered = filtered.filter((item) =>
          isTimeout ? item.timeoutMargin > 0 : item.timeoutMargin <= 0,
        );
      }

      const total = filtered.length;
      const start = (params.currentPage - 1) * params.pageSize;
      const end = start + params.pageSize;
      const pageData = filtered.slice(start, end);

      resolve(
        mockSuccess({
          tableData: pageData,
          paging: {
            currentPage: params.currentPage,
            totalCount: total,
            pageSize: params.pageSize,
          },
        }),
      );
    }, 400);
  });
}

/**
 * Mock 查询支付发票报表
 */
export function mockPagePaymentInvoice(
  params: PaymentInvoiceQueryParams,
): Promise<ApiResponse<PageData<PaymentInvoiceDTO>>> {
  return new Promise((resolve) => {
    setTimeout(() => {
      let filtered = [...paymentInvoiceData];

      if (params.storeNo) {
        filtered = filtered.filter((item) => item.storeNo.includes(params.storeNo!));
      }
      if (params.storeName) {
        filtered = filtered.filter((item) =>
          item.storeName.toLowerCase().includes(params.storeName!.toLowerCase()),
        );
      }
      if (params.paymentType) {
        filtered = filtered.filter((item) => item.paymentType === params.paymentType);
      }
      if (params.orderNumber) {
        filtered = filtered.filter((item) => item.orderNumber.includes(params.orderNumber!));
      }

      const total = filtered.length;
      const start = (params.currentPage - 1) * params.pageSize;
      const end = start + params.pageSize;
      const pageData = filtered.slice(start, end);

      resolve(
        mockSuccess({
          tableData: pageData,
          paging: {
            currentPage: params.currentPage,
            totalCount: total,
            pageSize: params.pageSize,
          },
        }),
      );
    }, 400);
  });
}
